/**
 * Extract a human-readable message from a caught error.
 *
 * FastAPI returns two shapes for the `detail` field on error responses:
 *   - a string (custom HTTPException from a route)
 *   - an ARRAY of Pydantic validation objects, each with the shape
 *     { type, loc, msg, input, url } (thrown by request-body validation)
 *
 * Almost every page in this app used to do
 *     toast({ title: error.response?.data?.detail || fallback })
 * which quietly worked for the string case and blew the whole PWA up on
 * a completely blank white screen for the array case — React refuses to
 * render a plain object as a child (error #31).
 *
 * This helper always returns a string. If we can't derive one, it
 * returns the caller-supplied fallback.
 */
export function extractErrorMessage(error, fallback = 'Something went wrong') {
  const detail = error?.response?.data?.detail;

  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }

  if (Array.isArray(detail)) {
    // Pydantic 422 — each entry has {type, loc, msg, input, url}. Prefer msg;
    // join up to the first three so the user sees more than one issue at once
    // without a wall of text.
    const msgs = detail
      .map((d) => (d && typeof d === 'object' ? d.msg : null))
      .filter((m) => typeof m === 'string' && m.trim());
    if (msgs.length) return msgs.slice(0, 3).join(' · ');
  }

  if (detail && typeof detail === 'object' && typeof detail.msg === 'string') {
    return detail.msg;
  }

  if (typeof error?.message === 'string' && error.message.trim()) {
    return error.message;
  }

  return fallback;
}
